import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { Building2, Users, Heart } from 'lucide-react';

interface IStatCardProps {
  label: string;
  value: string;
  icon: React.ReactNode;
  iconBgColor?: string;
  iconColor?: string;
  footer?: string;
}

export const StatCard: React.FC<IStatCardProps> = ({
  label,
  value,
  icon,
  iconBgColor = 'primary.light',
  iconColor = 'primary.main',
  footer,
}) => (
  <Paper
    variant="outlined"
    sx={{
      p: 3,
      borderRadius: 4,
      position: 'relative',
      overflow: 'hidden',
      transition: 'box-shadow 0.2s ease-in-out',
      '&:hover': { boxShadow: 2 },
    }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
      <Box>
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.5 }}
        >
          {label}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.02em' }}>
          {value}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: iconBgColor,
          color: iconColor,
        }}
      >
        {icon}
      </Box>
    </Box>
    {footer && (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          {footer}
        </Typography>
      </Box>
    )}
  </Paper>
);

interface IStatsCardsProps {
  activeUsers: number;
  totalOrganizations: number;
}

export const StatsCards: React.FC<IStatsCardsProps> = ({ activeUsers, totalOrganizations }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
      gap: 3,
      mb: 4,
    }}
  >
    <StatCard
      label="Total Organizations"
      value={String(totalOrganizations)}
      icon={<Building2 size={22} />}
      iconBgColor="rgba(16, 185, 129, 0.1)"
      iconColor="#10b981"
    />
    <StatCard
      label="Active Users"
      value={activeUsers.toLocaleString()}
      icon={<Users size={22} />}
      iconBgColor="rgba(59, 130, 246, 0.1)"
      iconColor="#3b82f6"
    />
    <StatCard
      label="Health Score"
      value="—"
      icon={<Heart size={22} />}
      iconBgColor="rgba(16, 185, 129, 0.1)"
      iconColor="#10b981"
      footer="Standard across all tenants"
    />
  </Box>
);
